import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import './App.css';
import { AuthProvider, useAuth, AuthContext } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ProductList from './pages/ProductList';
import LCACalculator from './pages/LCACalculator';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Navbar from './components/Navbar/Navbar';
import ImpersonationBanner from './components/ImpersonationBanner';
import Profile from './pages/Profile';
import ProductResults from './pages/ProductResults';
import AssessmentSummary from './pages/AssessmentSummary';
import Test from './pages/test';
import OnboardingModal from './components/onboardingmodal/OnboardingModal';
import AcceptInvite from './pages/AcceptInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import StaffRoute from './components/StaffRoute';
import Pricing from './pages/Pricing';
import SupplierDirectory from './pages/SupplierDirectory';
import SupplierCatalogue from './pages/SupplierCatalogue';
import Reports from './pages/Reports';
import CustomerFootprintReports from './pages/CustomerFootprintReports';
import CustomerFootprintReportDetail from './pages/CustomerFootprintReportDetail';
import AccountTypePrompt from './components/AccountTypePrompt';
import ManufacturerRequests from './pages/ManufacturerRequests';
import { SpeedInsights } from "@vercel/speed-insights/react"
import { Analytics } from "@vercel/analytics/react"
import ScrollToTop from './components/ScrollToTop';
import SubscriptionGate from './components/SubscriptionGate';
import { ConfirmProvider } from './components/ConfirmDialog';

// Split off the heavy, rarely-reached screens so they stop riding in the initial
// bundle. AdminPortal alone is ~100KB of source that only staff can ever open;
// the report builders and the public manufacturer portal are similar. Each
// becomes its own chunk, fetched the first time someone actually goes there.
const AdminPortal = lazy(() => import('./pages/AdminPortal'));
const ProductComparisonReport = lazy(() => import('./pages/ProductComparisonReport'));
const CustomerFootprintReportCreate = lazy(() => import('./pages/CustomerFootprintReportCreate'));
const ManufacturerPortalPublic = lazy(() => import('./pages/ManufacturerPortalPublic'));
const ComplianceUploadPublic = lazy(() => import('./pages/ComplianceUploadPublic'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const Compliance = lazy(() => import('./pages/Compliance'));
const QuestionnaireReview = lazy(() => import('./pages/QuestionnaireReview'));
const ExtensionConnect = lazy(() => import('./pages/ExtensionConnect'));
const Home = lazy(() => import('./pages/Home'));

// Shown while a route chunk is in flight. Matches the auth-init spinner so a
// chunk fetch doesn't read as a different kind of wait.
function RouteFallback() {
  return (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading…</span>
      </div>
    </div>
  );
}

// Guest Navbar for unauthenticated users
function GuestNavbar() {
  return (
    <nav className="navbar navbar-expand-sm px-3">
      <a className="navbar-brand fw-bold text-dark d-flex align-items-center gap-2" href="https://tryzilch.com/">
        <img
          src="https://froomyrsdtspkmaognle.supabase.co/storage/v1/object/public/logos/zilch-testing-store.myshopify.com-1753009531626.png"
          width="30"
          height="30"
          className="d-inline-block align-top"
          alt=""
        />
        Zilch.
      </a>
      <div className="ms-auto">
        <a 
          href="https://tryzilch.com/" 
          className="btn btn-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Get in Contact
        </a>
      </div>
    </nav>
  );
}

function AppContent() {
  const { user, loading, supplierInfo, billing, isStaff } = useAuth();
  const location = useLocation();

  // Pages a no-subscription org may still reach (so they can subscribe, manage
  // billing, view public/share pages, accept invites, or staff-admin). Every
  // other in-app route is replaced by the SubscriptionGate paywall.
  const billingGateExempt =
    location.pathname.startsWith('/billing') ||
    location.pathname === '/profile' ||
    location.pathname === '/pricing' ||
    location.pathname === '/terms' ||
    location.pathname === '/accept-invite' ||
    location.pathname === '/login' ||
    location.pathname.startsWith('/product-results/') ||
    location.pathname.startsWith('/results/') ||
    location.pathname.startsWith('/admin') ||
    // Compliance documents moved here from /profile, which is exempt, so they
    // must stay reachable on every plan; the AI actions are gated server-side.
    location.pathname === '/compliance' ||
    location.pathname.startsWith('/compliance/') ||
    location.pathname === '/extension/connect';

  // Block the app for orgs without a usable subscription. Trialing/past_due
  // still count as usable; staff, billing-override and enterprise are exempt.
  // While billing is still loading we render optimistically — the server guards
  // the actual write APIs, so there's no real bypass.
  const billingBlocked = (() => {
    if (isStaff) return false;
    if (!billing) return false;
    if (billing.is_override) return false;
    if (billing.plan === 'enterprise') return false;
    return !['active', 'trialing', 'past_due'].includes(billing.status);
  })();
  const isProductResultsPage = location.pathname.startsWith('/product-results/') || location.pathname.startsWith('/results/');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const onboardingTriggeredRef = useRef(false);
  const onboardingStorageKey = useMemo(() => {
    if (!user) return null;
    const id = user?.id || user?.email || 'default';
    return `zilch_onboarding_seen_${id}`;
  }, [user]);

  useEffect(() => {
    if (!user || !onboardingStorageKey) {
      setShowOnboarding(false);
      onboardingTriggeredRef.current = false;
      return;
    }
    if (onboardingTriggeredRef.current) return;
    const seen = localStorage.getItem(onboardingStorageKey) === '1';
    if (seen) return;
    const isOnboardingRoute = location.pathname === '/' || location.pathname === '/products';
    if (isOnboardingRoute) {
      setShowOnboarding(true);
      onboardingTriggeredRef.current = true;
    }
  }, [user, onboardingStorageKey, location.pathname]);

  const handleFinishOnboarding = () => {
    if (onboardingStorageKey) {
      localStorage.setItem(onboardingStorageKey, '1');
    }
    setShowOnboarding(false);
  };
  
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // TEMP: navbar dropdown visual preview harness, no auth required — remove before merge.
  if (location.pathname === '/__preview-navbar') {
    return (
      <AuthContext.Provider value={{
        isStaff: true,
        supplierInfo: { account_type: 'Supplier', supplier_id: 'preview' },
        getToken: async () => null,
        signOut: async () => {},
      }}>
        <Navbar />
        <div style={{ padding: 40 }}>Navbar preview harness</div>
      </AuthContext.Provider>
    );
  }

  // Public manufacturer portal renders standalone (no Navbar, no auth) — the
  // manufacturer has no account; access is the emailed token + code.
  if (location.pathname.startsWith('/m/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/m/:token" element={<ManufacturerPortalPublic />} />
        </Routes>
      </Suspense>
    );
  }

  // Public compliance document upload renders standalone for the same reason —
  // it's opened straight from an email, by someone who shouldn't have to log in
  // (and may not be the account holder). Access is the emailed token alone.
  if (location.pathname.startsWith('/compliance-upload/')) {
    return (
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/compliance-upload/:token" element={<ComplianceUploadPublic />} />
        </Routes>
      </Suspense>
    );
  }

  // Signup wizard renders standalone (no Navbar/Onboarding) regardless of auth
  // state — the wizard signs the user in mid-flow, so it must survive the
  // logged-out -> logged-in transition without unmounting.
  if (location.pathname === '/signup' || location.pathname === '/signup/enterprise') {
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup/enterprise" element={<Signup lockedAccountType="Enterprise" />} />
      </Routes>
    );
  }

  // Password reset renders standalone regardless of auth state: reset links get
  // opened on whichever device is to hand, which is often one that still has a
  // live session. The emailed token is the credential, not the session.
  if (location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
    return (
      <Routes>
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    );
  }

  return (
    <>
      {!user ? (
        <>
          {isProductResultsPage ? (
            <>
              <GuestNavbar />
              <Routes>
                <Route path="/product-results/:productId" element={<ProductResults />} />
                <Route path="/results/:productId" element={<ProductResults />} />
                <Route path="*" element={<Login />} />
              </Routes>
            </>
          ) : (
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/accept-invite" element={<AcceptInvite />} />
                <Route path="/terms" element={<TermsAndConditions />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/product-results/:productId" element={<ProductResults />} />
                <Route path="/results/:productId" element={<ProductResults />} />
                <Route path="*" element={<Login />} />
              </Routes>
            </Suspense>
          )}
        </>
      ) : (
        <>
          <ImpersonationBanner />
          <Navbar />
          <OnboardingModal open={showOnboarding} onFinish={handleFinishOnboarding} />
          <AccountTypePrompt />
          {billingBlocked && !billingGateExempt ? (
            <SubscriptionGate />
          ) : (
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductList /></ProtectedRoute>} />
            <Route path="/manufacturers" element={<ProtectedRoute><ManufacturerRequests /></ProtectedRoute>} />
            <Route path="/suppliers" element={<ProtectedRoute><SupplierDirectory /></ProtectedRoute>} />
            <Route path="/suppliers/:supplierId/catalogue" element={<ProtectedRoute><SupplierCatalogue /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
            <Route path="/reports/customer-footprint" element={<ProtectedRoute><CustomerFootprintReports /></ProtectedRoute>} />
            <Route path="/reports/customer-footprint/new" element={<ProtectedRoute><CustomerFootprintReportCreate /></ProtectedRoute>} />
            <Route path="/reports/customer-footprint/:id" element={<ProtectedRoute><CustomerFootprintReportDetail /></ProtectedRoute>} />
            <Route path="/reports/product-comparison" element={<ProtectedRoute><ProductComparisonReport /></ProtectedRoute>} />
            <Route path="/lca-calculator/:productId" element={<ProtectedRoute><LCACalculator /></ProtectedRoute>} />
            <Route path="/assessment-summary/:productId" element={<ProtectedRoute><AssessmentSummary /></ProtectedRoute>} />
            <Route path="/compliance" element={<ProtectedRoute><Compliance /></ProtectedRoute>} />
            <Route path="/compliance/questionnaires/:runId" element={<ProtectedRoute><QuestionnaireReview /></ProtectedRoute>} />
            <Route path="/extension/connect" element={<ProtectedRoute><ExtensionConnect /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/billing" element={<ProtectedRoute>{supplierInfo?.role === 'member' ? <Navigate to="/profile" replace /> : <Profile />}</ProtectedRoute>} />
            <Route path="/billing/success" element={<ProtectedRoute>{supplierInfo?.role === 'member' ? <Navigate to="/profile" replace /> : <Profile />}</ProtectedRoute>} />
            <Route path="/billing/cancel" element={<ProtectedRoute>{supplierInfo?.role === 'member' ? <Navigate to="/profile" replace /> : <Profile />}</ProtectedRoute>} />
            <Route path="/accept-invite" element={<AcceptInvite />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/product-results/:productId" element={<ProductResults />} />
            <Route path="/results/:productId" element={<ProductResults />} />
            <Route path="/test" element={<Test />} />
            <Route path="/admin" element={<StaffRoute><AdminPortal /></StaffRoute>} />
            <Route path="/admin/orgs/:orgId" element={<StaffRoute><AdminPortal /></StaffRoute>} />
            <Route path="/admin/orgs/:orgId/assessments/:assessmentId" element={<StaffRoute><AdminPortal /></StaffRoute>} />
          </Routes>
          </Suspense>
          )}
        </>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <ScrollToTop />
      <SpeedInsights />
      <Analytics />
      <AuthProvider>
        <ConfirmProvider>
          <AppContent />
        </ConfirmProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;